import React from 'react';
import PropTypes from 'prop-types';
import './Loader.scss';

/**
 * Reusable Loader component for displaying loading states throughout the application.
 *
 * Features:
 * - Multiple sizes (small, medium, large, xl)
 * - Multiple variants (spinner, dots, pulse)
 * - Custom text messages
 * - Glassmorphism styling
 * - Responsive design
 * - Accessibility support
 *
 * @example
 * // Basic spinner
 * <Loader />
 *
 * // Custom size and text
 * <Loader size="large" text="Loading contracts..." />
 *
 * // Dots variant
 * <Loader variant="dots" text="Processing..." />
 *
 * // Inline loader
 * <Loader size="small" inline />
 */
export const Loader = ({
  size = 'medium',
  variant = 'spinner',
  text = null,
  inline = false,
  className = '',
  showText = true,
  ...props
}) => {
  const getLoaderContent = () => {
    switch (variant) {
      case 'dots':
        return (
          <div className='gigacore-loader-dots'>
            <div className='gigacore-loader-dot'></div>
            <div className='gigacore-loader-dot'></div>
            <div className='gigacore-loader-dot'></div>
          </div>
        );

      case 'pulse':
        return (
          <div className='gigacore-loader-pulse'>
            <div className='gigacore-loader-pulse-circle'></div>
          </div>
        );

      case 'spinner':
      default:
        return <div className='gigacore-loader-spinner'></div>;
    }
  };

  const classes = [
    'gigacore-loader',
    `gigacore-loader--${size}`,
    `gigacore-loader--${variant}`,
    inline && 'gigacore-loader--inline',
    !showText && 'gigacore-loader--no-text',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role='status'
      aria-label={text || 'Loading'}
      {...props}
    >
      {getLoaderContent()}
      {showText && text && <p className='gigacore-loader-text'>{text}</p>}
    </div>
  );
};

Loader.propTypes = {
  /** Size of the loader */
  size: PropTypes.oneOf(['small', 'medium', 'large', 'xl']),

  /** Visual variant of the loader */
  variant: PropTypes.oneOf(['spinner', 'dots', 'pulse']),

  /** Text to display below the loader */
  text: PropTypes.string,

  /** Whether the loader should be displayed inline */
  inline: PropTypes.bool,

  /** Whether to show text when provided */
  showText: PropTypes.bool,

  /** Additional CSS classes */
  className: PropTypes.string,
};
